import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Immutable from 'immutable';
import InlineSVG from 'svg-inline-react';

import { EXPECTED_PRICE_TRACKER_DATE_FORMAT } from 'constants/dates';
import { getBoardBasisLabel, dayInMilliseconds } from 'utils/utils';
import { formatTime } from 'utils/book';
import dates from 'utils/dates';
import * as optimizelyUtil from 'utils/optimizely';
import { getUserClassArray } from 'utils/dataLayerHelpers';
import { GTM_EVENT_NAMES } from 'utils/gtm';
import LHX from 'utils/LHX';
import * as priceTrackerActions from 'actions/priceTrackerActions';
import * as tagActions from 'actions/tagActions';
import PriceTrackerSummary from 'components/PriceTrackerSummary/PriceTrackerSummary';
import LinkButton from 'components/LinkButton/LinkButton';
import EmailSignUp from 'components/EmailSignUp/EmailSignUp';
import SubpageMasthead from 'components/SubpageMasthead/SubpageMasthead';
import Loading from 'components/Loading/Loading';
import validationRegexes from 'constants/validationRegexes';

import './PriceTrackerSignUpLayout.mobile.scss';
import './PriceTrackerSignUpLayout.desktop.scss';
import alertIcon from './images/bell.svg';

/* istanbul ignore next */
const mapStateToProps = state => ({
  appState: state.appState,
  baseState: state.baseState,
  bookState: state.bookState,
  hotelDetails: state.hotelDetails,
  priceTracker: state.priceTracker,
  featureFlags: state.featureFlags,
});

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return {
    priceTrackerActionsBound: bindActionCreators(priceTrackerActions, dispatch),
    tagActionsBound: bindActionCreators(tagActions, dispatch),
  };
}

export class PriceTrackerSignUpLayout extends Component {
  static propTypes = {
    appState: PropTypes.object,
    baseState: PropTypes.object,
    bookState: PropTypes.object,
    hotelDetails: PropTypes.object,
    isCheapestFlight: PropTypes.bool,
    isModal: PropTypes.bool,
    location: PropTypes.object.isRequired,
    priceTracker: PropTypes.object,
    priceTrackerActionsBound: PropTypes.object,
    featureFlags: PropTypes.object,
    tagActionsBound: PropTypes.object,
    route: PropTypes.object,
    routes: PropTypes.arrayOf(PropTypes.shape({
      pageName: PropTypes.string,
    })),
  }

  static defaultProps = {
    appState: Immutable.Map(),
    baseState: Immutable.Map(),
    bookState: Immutable.Map(),
    featureFlags: Immutable.Map(),
    hotelDetails: Immutable.Map(),
    priceTracker: Immutable.Map(),
    route: {},
    routes: [],
  }

  state = {
    emailModified: false,
    fullNameModified: false,
    submitted: false,
  }

  componentDidMount() {
    this.fireOptimizely('clicked');
  }

  fireOptimizely = (message) => {
    optimizelyUtil.fireEvent('PriceTrackerSignUp', message);
  };

  isValidName = value => value.length > 2;

  isValidEmail = value => validationRegexes.email.test(value) && value.length > 5;

  get validation() {
    const { emailModified, fullNameModified } = this.state;
    return {
      confirmName: {
        isValid: value => !fullNameModified ? null : this.isValidName(value),
        message: 'Please provide a valid name',
      },
      confirmEmail: {
        isValid: value => !emailModified ? null : this.isValidEmail(value),
        message: 'Please provide a valid email address',
      },
    };
  }

  get isLoading() {
    return this.props.priceTracker.get('request') === 'pending';
  }

  get isSuccess() {
    const isSuccessful = this.props.priceTracker.get('request') === 'success';
    if (isSuccessful) {
      this.fireOptimizely('selected');
    }
    return isSuccessful;
  }

  get isError() {
    return this.props.priceTracker.get('request') === 'failure';
  }

  get isData() {
    const { appState, bookState } = this.props;
    return !!appState.get('selectedOffer') || !!bookState.get('data');
  }

  get isFormValid() {
    const { emailModified, fullNameModified } = this.state;
    const { priceTracker } = this.props;
    const { confirmName, confirmEmail } = this.validation;
    return emailModified && fullNameModified && confirmName.isValid(priceTracker.get('fullName')) && confirmEmail.isValid(priceTracker.get('email'));
  }

  get endpoint() {
    const { location } = this.props;
    const url = '/api/pricetracker/email';
    return (location.pathname.split('/')[1] === 'book') ? `/book${url}` : url;
  }

  replaceUrlParam(url, paramName, paramValue = '') {
    const pattern = new RegExp(`\\b(${paramName}=).*?(&|$)`);
    let finalUrl = url;
    if (url.search(pattern) >= 0) {
      finalUrl = url.replace(pattern, `$1${paramValue}$2`);
    } else {
      finalUrl = `${url}${url.indexOf('?') > 0 ? '&' : '?'}${paramName}=${paramValue}`;
    }

    return finalUrl;
  }

  get optimizelyParams() {
    const optimizely = window.optimizely.data;
    const optimizelyState = !!optimizely && !!optimizely.state ? optimizely.state : {};
    const activeExperiments = optimizelyState.activeExperiments || [];
    const variationMap = optimizelyState.variationMap || {};
    return activeExperiments.map(id => [`optimizely_x${id}`, variationMap[id]]);
  }

  get userClass() {
    const userClass = getUserClassArray(LHX.userClass);
    return userClass ? userClass.join('') : '';
  }

  onSubmit = (event) => {
    event.preventDefault();

    if (this.isFormValid) {
      const {
        backURL,
        calDate,
        endpoint,
        offerSummary,
        optimizelyParams,
        props,
        replaceUrlParam,
        userClass,
      } = this;

      const { priceTrackerActionsBound, appState, priceTracker, tagActionsBound, location, route } = props;
      let pageToTrackUrl = replaceUrlParam(backURL, 'calDate', calDate);
      pageToTrackUrl = replaceUrlParam(pageToTrackUrl, 'ch', 'sha');

      optimizelyParams.forEach((item) => {
        pageToTrackUrl = replaceUrlParam(pageToTrackUrl, item[0], item[1]);
      });

      const formData = {
        email: priceTracker.get('email'),
        userClass,
        name: priceTracker.get('fullName'),
        pageToTrackUrl,
        subscribeToNewsletter: priceTracker.get('newsletterSubscription'),
        hotelName: offerSummary.hotelName,
        resort: offerSummary.destination,
        nights: offerSummary.duration,
        offerState: offerSummary.offerState,
        boardBasis: offerSummary.boardBasis,
        outbound: {
          flightNumber: offerSummary.flightDetails.outboundFlightNumber,
          departureDate: offerSummary.flightDetails.outboundDate,
          arrivalDate: offerSummary.flightDetails.outboundArrivalDate,
          departureTime: offerSummary.flightDetails.outboundDepartureTime,
          arrivalTime: offerSummary.flightDetails.outboundArrivalTime,
          airport: offerSummary.flightDetails.outboundDepartureName,
        },
        inbound: {
          flightNumber: offerSummary.flightDetails.inboundFlightNumber,
          departureDate: offerSummary.flightDetails.inboundDate,
          arrivalDate: offerSummary.flightDetails.inboundArrivalDate,
          departureTime: offerSummary.flightDetails.inboundDepartureTime,
          arrivalTime: offerSummary.flightDetails.inboundArrivalTime,
          airport: offerSummary.flightDetails.inboundDepartureName,
        },
        price: appState.getIn(['selectedOffer', 'price']),
      };

      priceTrackerActionsBound.postSignUp(formData, endpoint);

      if (priceTracker.get('newsletterSubscription')) {
        tagActionsBound.fireGTMEvent(GTM_EVENT_NAMES.TRACK_PRICE_TRACKER, location, route);
      }

      this.setState({
        submitted: true,
      });
    } else {
      this.setState({
        emailModified: true,
        fullNameModified: true,
      });
    }
  }

  onNameBlur = () => {
    this.setState({
      fullNameModified: true,
    });
  }

  onEmailBlur = () => {
    this.setState({
      emailModified: true,
    });
  }

  toggleSubscribe = () => {
    const { priceTracker, priceTrackerActionsBound } = this.props;
    priceTrackerActionsBound.setNewsletterSubscription(!priceTracker.get('newsletterSubscription'));
  }

  setNameField = (event) => {
    this.props.priceTrackerActionsBound.setFullName(event.currentTarget.value);
  }

  setEmailField = (event) => {
    this.props.priceTrackerActionsBound.setEmail(event.currentTarget.value);
  }

  get inboundArrivalName() {
    const { appState, bookState } = this.props;
    return bookState.getIn(['data', 'flight', 'inbound', 'arrvName']) || appState.getIn(['selectedOffer', 'departureAirport']);
  }

  get inboundArrivalTime() {
    const { appState, bookState } = this.props;
    return formatTime(bookState.getIn(['data', 'flight', 'inbound', 'arrvTime'], '')) || appState.getIn(['selectedOffer', 'returnArrivalTime']);
  }

  get inboundDate() {
    const { appState, bookState } = this.props;
    const inboundDate = bookState.getIn(['data', 'flight', 'inbound', 'deptDate']) || appState.getIn(['selectedOffer', 'returnDepartureDate']);
    return dates.format(inboundDate, EXPECTED_PRICE_TRACKER_DATE_FORMAT);
  }

  get inboundArrivalDate() {
    const { appState, bookState } = this.props;
    const inboundDate = bookState.getIn(['data', 'flight', 'inbound', 'arrvDate']) || appState.getIn(['selectedOffer', 'returnArrivalDate']);
    return dates.format(inboundDate, EXPECTED_PRICE_TRACKER_DATE_FORMAT);
  }

  get inboundDepartureName() {
    const { appState, bookState } = this.props;
    return bookState.getIn(['data', 'flight', 'inbound', 'deptName']) || appState.getIn(['selectedOffer', 'returnAirport']);
  }

  get inboundDepartureTime() {
    const { appState, bookState } = this.props;
    return formatTime(bookState.getIn(['data', 'flight', 'inbound', 'deptTime'], '')) || appState.getIn(['selectedOffer', 'returnDepartureTime']);
  }

  get inboundFlightNumber() {
    const { appState, bookState } = this.props;
    return bookState.getIn(['data', 'flight', 'inbound', 'number']) || appState.getIn(['selectedOffer', 'returnFlightNumber']);
  }

  get outboundArrivalName() {
    const { appState, bookState } = this.props;
    return bookState.getIn(['data', 'flight', 'outbound', 'arrvName']) || appState.getIn(['selectedOffer', 'returnAirport']);
  }

  get outboundArrivalTime() {
    const { appState, bookState } = this.props;
    return formatTime(bookState.getIn(['data', 'flight', 'outbound', 'arrvTime'], '')) || appState.getIn(['selectedOffer', 'arrivalTime']);
  }

  get outboundArrivalDate() {
    const { appState, bookState } = this.props;
    const inboundDate = bookState.getIn(['data', 'flight', 'outbound', 'arrvDate']) || appState.getIn(['selectedOffer', 'arrivalDate']);
    return dates.format(inboundDate, EXPECTED_PRICE_TRACKER_DATE_FORMAT);
  }

  get outboundDate() {
    const { appState, bookState } = this.props;
    const inboundDate = bookState.getIn(['data', 'flight', 'outbound', 'deptDate']) || appState.getIn(['selectedOffer', 'departureDate']);
    return dates.format(inboundDate, EXPECTED_PRICE_TRACKER_DATE_FORMAT);
  }

  get outboundDepartureName() {
    const { appState, bookState } = this.props;
    return bookState.getIn(['data', 'flight', 'outbound', 'deptName']) || appState.getIn(['selectedOffer', 'departureAirport']);
  }

  get outboundDepartureTime() {
    const { appState, bookState } = this.props;
    return formatTime(bookState.getIn(['data', 'flight', 'outbound', 'deptTime'], '')) || appState.getIn(['selectedOffer', 'departureTime']);
  }

  get outboundFlightNumber() {
    const { appState, bookState } = this.props;
    return bookState.getIn(['data', 'flight', 'outbound', 'number']) || appState.getIn(['selectedOffer', 'flightNumber']);
  }

  get destination() {
    const { hotelDetails, bookState } = this.props;
    const area = hotelDetails.get('D_DestLevel3') || hotelDetails.get('D_DestLevel2');
    const destination = `${hotelDetails.get('D_Resort')}, ${area}`;
    return bookState.getIn(['data', 'destination']) || destination;
  }

  get hotelName() {
    const { hotelDetails, bookState } = this.props;
    return bookState.getIn(['data', 'hotel_name']) || hotelDetails.get('name');
  }

  get boardBasis() {
    const { appState, bookState } = this.props;
    const roomId = bookState.getIn(['booking_info', 'selection', 'room_selected', 0]);
    return getBoardBasisLabel(bookState.getIn(['entities', 'rooms', `${roomId}`, 'board_basis'])) || appState.getIn(['selectedOffer', 'boardBasis']);
  }

  get duration() {
    const { appState, bookState } = this.props;
    return bookState.getIn(['data', 'num_nights']) || appState.getIn(['selectedOffer', 'nights']);
  }

  get offerState() {
    const { appState } = this.props;
    return appState.getIn(['selectedOffer', 'offerState']);
  }

  get calDate() {
    const { bookState, baseState } = this.props;

    const baseDate = baseState.get('calDateValue');
    const bookDate = new Date(bookState.getIn(['data', 'flight', 'outbound', 'deptDate']));

    return baseDate || Date.UTC(bookDate.getFullYear(), bookDate.getMonth(), bookDate.getDate()) / dayInMilliseconds;
  }

  get backURL() {
    const { location } = this.props;
    let urlPrefix;

    if (location.pathname.split('/')[1] === 'book') {
      urlPrefix = '/book/flight-and-hotel/offer-summary';
    } else {
      urlPrefix = location.pathname.split('/sub-page/price-tracker')[0];
    }

    const origin = `${window.location.protocol}//${window.location.host}`;
    return `${origin}${urlPrefix}.html${location.search}`;
  }

  get offerSummary() {
    const flightDetails = {
      inboundArrivalName: this.inboundArrivalName,
      inboundArrivalTime: this.inboundArrivalTime,
      inboundArrivalDate: this.inboundArrivalDate,
      inboundDate: this.inboundDate,
      inboundDepartureName: this.inboundDepartureName,
      inboundDepartureTime: this.inboundDepartureTime,
      inboundFlightNumber: this.inboundFlightNumber,
      outboundArrivalName: this.outboundArrivalName,
      outboundArrivalTime: this.outboundArrivalTime,
      outboundArrivalDate: this.outboundArrivalDate,
      outboundDate: this.outboundDate,
      outboundDepartureName: this.outboundDepartureName,
      outboundDepartureTime: this.outboundDepartureTime,
      outboundFlightNumber: this.outboundFlightNumber,
    };

    return {
      boardBasis: this.boardBasis,
      destination: this.destination,
      duration: this.duration,
      offerState: this.offerState,
      flightDetails,
      hotelName: this.hotelName,
      isCheapestFlight: this.props.isCheapestFlight,
    };
  }

  render() {
    const offerSummary = this.offerSummary;

    const { submitted } = this.state;

    const { isModal, priceTracker, routes } = this.props;
    const isBookRoute = routes.some(route => route.pageName === 'book');
    const isInBookPage = isBookRoute || isModal;
    const priceAlertDays = isInBookPage ? '7' : '30';

    return (<div className="price-tracker-signup">

      { !isModal && (
        <SubpageMasthead location={location} ctaType="back" backUrl={this.backURL}>
          <div className="price-tracker-signup__heading__title">
            <InlineSVG src={alertIcon} className="price-tracker-signup__heading__icon" />
            {priceAlertDays} Day Price Alert
          </div>
        </SubpageMasthead>
      ) }

      { !this.isData && (
        <div className="price-tracker-signup__message--loading">
          <Loading loadingState="spinning" />
        </div>
      ) }

      { !submitted && this.isData && (
        <PriceTrackerSummary
          boardBasis={offerSummary.boardBasis}
          destination={offerSummary.destination}
          duration={offerSummary.duration}
          offerState={offerSummary.offerState}
          flightDetails={offerSummary.flightDetails}
          hotelName={offerSummary.hotelName}
          isCheapestFlight={offerSummary.isCheapestFlight}
        />
      ) }

      { !submitted && this.isData && (
        <EmailSignUp
          email={priceTracker.get('email', '')}
          formName="emailSignUp"
          fullName={priceTracker.get('fullName', '')}
          onEmailBlur={this.onEmailBlur}
          onNameBlur={this.onNameBlur}
          onSubmit={this.onSubmit}
          setEmailField={this.setEmailField}
          setNameField={this.setNameField}
          setSubscribe={this.toggleSubscribe}
          subscribe={priceTracker.get('newsletterSubscription', true)}
          validation={this.validation}
          displayDisclaimer={isInBookPage}
        />
      ) }

      { submitted && this.isSuccess && (
        <div className="price-tracker-signup__message--success">
          <div className="price-tracker-signup__message__text">
            <h3>Thank you for signing up for Price Alerts!</h3>
            <p>Be sure to look out for emails in your inbox.</p>
          </div>
          { !isModal && (
          <LinkButton to={this.backURL} type="secondary" size="small">
              Click here to close now
            </LinkButton>
          ) }
        </div>
      ) }

      { submitted && this.isError && (
        <div className="price-tracker-signup__message--error">
          <div className="price-tracker-signup__message__text">
            <h3>Oops! Something has gone wrong.</h3>
            <p>Please try again.</p>
          </div>
        </div>
      ) }

      { submitted && this.isLoading && (
        <div className="price-tracker-signup__message--loading">
          <Loading loadingState="spinning" />
        </div>
      ) }

    </div>);
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(PriceTrackerSignUpLayout);



/** WEBPACK FOOTER **
 ** ./src/layouts/PriceTrackerSignUpLayout/PriceTrackerSignUpLayout.jsx
 **/